import { useState, type ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import initials from '../utils/initials';

const box = cva(['shrink-0'], {
  variants: {
    size: {
      6: 'size-6 text-[10px]',
      8: 'h-8 w-8 text-xs',
      10: 'h-10 w-10 text-sm',
      12: 'h-12 w-12 text-sm',
      16: 'h-16 w-16 text-lg',
    },
    rounded: {
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      full: 'rounded-full',
    },
  },
});

// An image, or the initials of `name` (else `icon`) when it is missing or
// fails to load.
export default function KoobAvatar({
  className,
  src,
  name,
  icon,
  alt = '',
  size = 10,
  rounded = 'md',
  fit = 'cover',
}: {
  className?: string;
  src?: string | null;
  name?: string | null;
  icon?: ReactNode;
  alt?: string;
  size?: 6 | 8 | 10 | 12 | 16;
  rounded?: 'sm' | 'md' | 'lg' | 'full';
  /** contain: a logo, shown whole on white inside a border. */
  fit?: 'cover' | 'contain';
}) {
  const [broken, setBroken] = useState<string | null>(null);
  const classes = [box({ size, rounded }), className].filter(Boolean).join(' ');

  if (src && broken !== src) {
    return (
      <img
        className={`${classes} ${
          fit === 'contain'
            ? 'border border-solid border-k-gray bg-white object-contain'
            : 'object-cover'
        }`}
        src={src}
        alt={alt}
        onError={() => setBroken(src)}
      />
    );
  }

  return (
    <span
      aria-hidden
      className={`${classes} flex items-center justify-center bg-navy-800 font-semibold text-white`}
    >
      {initials(name ?? '') || icon}
    </span>
  );
}
